import { useEffect, useRef, useState, useCallback } from 'react';
import { booleanPointInPolygon, point as turfPoint, polygon as turfPolygon } from '@turf/turf';
import {
  streetsInBbox, createStreet, deleteStreet,
  obstaclesInBbox, createObstacle, deleteObstacle,
  polesInBbox, createPole, deletePole,
  setBuildingEntry,
} from '../../lib/infrastructureApi';
import { checkPolygon } from '../../utils/overlapCheck';

// Phase 2a operator tools. Each tool is self-contained: a `<...Tool active />`
// component that wires its own map listeners and HUD when `active` is true,
// and unwires when inactive. The parent (AnnotationMap) owns the one-tool-at-
// a-time mutual exclusion via a single `tool` string state and calls onSaved
// after a successful write so the parent can refresh the on-map layers.

// =========================================================================
// Shared dark-glass HUD styles - match the existing DrawHUD aesthetic.
// =========================================================================
const HUD_BASE = {
  position: 'absolute',
  top: 14,
  insetInlineStart: '50%',
  transform: 'translateX(-50%)',
  zIndex: 6,
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  minWidth: 300,
  maxWidth: 360,
  padding: '12px 14px',
  background: 'linear-gradient(135deg, rgba(15,23,42,0.95) 0%, rgba(30,41,59,0.93) 100%)',
  borderRadius: 13,
  border: '1px solid rgba(148,163,184,0.24)',
  boxShadow:
    '0 1px 0 rgba(255,255,255,0.07) inset, 0 10px 30px rgba(2,6,23,0.6), 0 1px 3px rgba(0,0,0,0.45)',
  backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)',
  direction: 'rtl',
  fontFamily: "'Vazirmatn', sans-serif",
};
const BTN_PRIMARY = (hover, disabled, accent = 'teal') => {
  const grad = {
    teal: hover
      ? 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)'
      : 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
    rose: hover
      ? 'linear-gradient(135deg, #f43f5e 0%, #e11d48 100%)'
      : 'linear-gradient(135deg, #e11d48 0%, #be123c 100%)',
    sky: hover
      ? 'linear-gradient(135deg, #38bdf8 0%, #0ea5e9 100%)'
      : 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
    amber: hover
      ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
      : 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
  };
  return {
    flex: 1,
    padding: '9px 14px',
    background: disabled ? 'rgba(51,65,85,0.5)' : grad[accent] || grad.teal,
    color: disabled ? '#64748b' : '#f0fdfa',
    fontFamily: "'Vazirmatn', sans-serif",
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1,
    border: disabled ? '1px solid rgba(148,163,184,0.2)' : '1px solid rgba(45,212,191,0.4)',
    borderRadius: 8,
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'background 140ms ease',
  };
};
const BTN_GHOST = (hover) => ({
  padding: '9px 14px',
  background: hover ? 'rgba(71,85,105,0.7)' : 'rgba(51,65,85,0.55)',
  color: '#e2e8f0',
  fontFamily: "'Vazirmatn', sans-serif",
  fontSize: 13,
  fontWeight: 600,
  lineHeight: 1,
  border: '1px solid rgba(148,163,184,0.32)',
  borderRadius: 8,
  cursor: 'pointer',
  transition: 'background 140ms ease',
});

// Distance in metres between two lat/lng coords (haversine).
function havM(aLat, aLng, bLat, bLng) {
  const R = 6371000;
  const t = (x) => (x * Math.PI) / 180;
  const dLat = t(bLat - aLat);
  const dLng = t(bLng - aLng);
  const s = Math.sin(dLat / 2) ** 2
    + Math.cos(t(aLat)) * Math.cos(t(bLat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(s));
}

// Snap a click coordinate to the nearest existing endpoint within `maxM`
// metres, or return the original coord unchanged. `endpoints` is an array of
// {lat,lng}. Returns the snapped or original coord, plus a boolean.
function snapToEndpoint(coord, endpoints, maxM = 8) {
  if (!Array.isArray(endpoints) || endpoints.length === 0) return { coord, snapped: false };
  let best = null;
  for (const p of endpoints) {
    const d = havM(coord.lat, coord.lng, p.lat, p.lng);
    if (best == null || d < best.d) best = { d, p };
  }
  if (best && best.d <= maxM) return { coord: { lat: best.p.lat, lng: best.p.lng }, snapped: true };
  return { coord, snapped: false };
}

// =========================================================================
// StreetDrawTool - polyline drawing for the road / alley network.
// =========================================================================
const STREET_CLASS_FA = {
  main: 'خیابان اصلی',
  local: 'خیابان فرعی',
  alley: 'کوچه',
  pedestrian: 'پیاده‌رو',
};
// Distinct HUES, not just lightness — over satellite imagery a slate-grey
// "local" line (old #64748b) read as the same blue family as the main road and
// the two were hard to tell apart. Blue = arterial, orange = secondary mirrors
// the Google/OSM road-hierarchy convention; alley stays a recessive grey and
// pedestrian stays the dashed violet. All four are kept clear of the teal
// building fill (#0d9488), the amber reported stroke (#f59e0b) and the red
// obstacle fill (#dc2626).
const STREET_CLASS_COLOR = {
  main: '#2563eb',
  local: '#ea580c',
  alley: '#94a3b8',
  pedestrian: '#a78bfa',
};

export function StreetDrawTool({
  map, active, snapPoints, onClose, onSaved,
}) {
  const [klass, setKlass] = useState('local');
  const [count, setCount] = useState(0);
  const [hover, setHover] = useState({ fin: false, undo: false, cancel: false });
  const vertsRef = useRef([]);
  const polyRef = useRef(null);
  const drawRenderRef = useRef(() => {});
  // Live state read inside the click listener via refs so that pan/zoom (which
  // rebuilds `snapPoints` via the InfrastructureLayer idle reload) or a
  // mid-draw klass switch never re-runs the setup effect — that would wipe
  // vertsRef.current and the operator would lose all their already-placed
  // points. The setup effect now keys only on [map, active]; class colour and
  // snap points propagate through these refs.
  const klassRef = useRef(klass);
  const snapPointsRef = useRef(snapPoints);
  useEffect(() => { klassRef.current = klass; }, [klass]);
  useEffect(() => { snapPointsRef.current = snapPoints; }, [snapPoints]);

  // Recolour the in-flight polyline whenever class changes, without tearing
  // down the listener / vertex state.
  useEffect(() => {
    if (polyRef.current) {
      polyRef.current.setOptions({ strokeColor: STREET_CLASS_COLOR[klass] || '#64748b' });
    }
  }, [klass]);

  useEffect(() => {
    if (!map || !active) return undefined;
    const prevCursor = map.get('draggableCursor');
    map.setOptions({ draggableCursor: 'crosshair', disableDoubleClickZoom: true });
    vertsRef.current = [];

    const render = () => {
      const verts = vertsRef.current;
      if (!polyRef.current) {
        polyRef.current = new window.google.maps.Polyline({
          clickable: false,
          strokeColor: STREET_CLASS_COLOR[klassRef.current] || '#64748b',
          strokeOpacity: 0.95,
          strokeWeight: 4,
          map,
        });
      }
      polyRef.current.setPath(verts);
      setCount(verts.length);
    };
    drawRenderRef.current = render;

    const onClick = (e) => {
      if (!e.latLng) return;
      const raw = { lat: e.latLng.lat(), lng: e.latLng.lng() };
      const { coord } = snapToEndpoint(raw, snapPointsRef.current || [], 8);
      vertsRef.current.push(coord);
      render();
    };
    const cl = map.addListener('click', onClick);
    render();

    return () => {
      window.google.maps.event.removeListener(cl);
      map.setOptions({ draggableCursor: prevCursor || null, disableDoubleClickZoom: false });
      if (polyRef.current) { polyRef.current.setMap(null); polyRef.current = null; }
      drawRenderRef.current = () => {};
    };
  }, [map, active]);

  const undoVertex = useCallback(() => {
    if (vertsRef.current.length === 0) return;
    vertsRef.current = vertsRef.current.slice(0, -1);
    drawRenderRef.current();
  }, []);

  const finish = useCallback(async () => {
    const verts = vertsRef.current;
    if (verts.length < 2) return;
    const geometry = {
      type: 'LineString',
      coordinates: verts.map((p) => [p.lng, p.lat]),
    };
    const { error } = await createStreet(geometry, { class: klass });
    if (error) {
      // eslint-disable-next-line no-alert
      window.alert('ذخیره‌ی خیابان ناموفق بود');
      return;
    }
    if (onSaved) onSaved();
    if (onClose) onClose();
  }, [klass, onSaved, onClose]);

  if (!active) return null;
  const canFinish = count >= 2;
  return (
    <div style={HUD_BASE}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: '#e2e8f0' }}>
        رسم خیابان / کوچه
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {Object.entries(STREET_CLASS_FA).map(([k, label]) => {
          const sel = klass === k;
          return (
            <button
              key={k}
              type="button"
              onClick={() => setKlass(k)}
              style={{
                flex: 1,
                minWidth: 70,
                padding: '6px 8px',
                background: sel ? STREET_CLASS_COLOR[k] : 'rgba(51,65,85,0.55)',
                color: sel ? '#f8fafc' : '#cbd5e1',
                fontFamily: "'Vazirmatn', sans-serif",
                fontSize: 11.5,
                fontWeight: sel ? 700 : 600,
                lineHeight: 1.2,
                border: `1px solid ${sel ? 'rgba(248,250,252,0.4)' : 'rgba(148,163,184,0.28)'}`,
                borderRadius: 6,
                cursor: 'pointer',
                transition: 'background 140ms ease',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: 11.5, color: '#94a3b8', lineHeight: 1.55 }}>
        روی نقشه کلیک کنید تا گره‌ها افزوده شوند. نزدیک سرِ خیابان‌های موجود کلیک کنید تا تقاطع به‌درستی متصل شود.
      </div>
      <div style={{ fontSize: 11.5, color: '#cbd5e1' }}>
        {count === 0 ? 'هنوز گره‌ای ثبت نشده' : `${count} گره ثبت شد`}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={finish}
          disabled={!canFinish}
          onMouseEnter={() => setHover((h) => ({ ...h, fin: true }))}
          onMouseLeave={() => setHover((h) => ({ ...h, fin: false }))}
          style={BTN_PRIMARY(hover.fin, !canFinish, 'teal')}
        >
          اتمام
        </button>
        <button
          type="button"
          onClick={undoVertex}
          disabled={count === 0}
          onMouseEnter={() => setHover((h) => ({ ...h, undo: true }))}
          onMouseLeave={() => setHover((h) => ({ ...h, undo: false }))}
          style={BTN_GHOST(hover.undo)}
        >
          حذف گره
        </button>
        <button
          type="button"
          onClick={onClose}
          onMouseEnter={() => setHover((h) => ({ ...h, cancel: true }))}
          onMouseLeave={() => setHover((h) => ({ ...h, cancel: false }))}
          style={BTN_GHOST(hover.cancel)}
        >
          لغو
        </button>
      </div>
    </div>
  );
}

// =========================================================================
// ObstacleDrawTool - polygon drawing for no-cross zones.
// =========================================================================
const OBS_KIND_FA = {
  river: 'رودخانه',
  rail: 'ریل قطار',
  highway: 'بزرگراه',
  restricted: 'محدوده‌ی ممنوع',
  other: 'سایر',
};

export function ObstacleDrawTool({
  map, active, getBuildingGeometries, onClose, onSaved,
}) {
  const [kind, setKind] = useState('other');
  const [count, setCount] = useState(0);
  const [overlap, setOverlap] = useState(false);
  const [hover, setHover] = useState({ fin: false, undo: false, cancel: false });
  const vertsRef = useRef([]);
  const polyRef = useRef(null);
  const drawRenderRef = useRef(() => {});
  // Live read from inside the click listener: same defensive pattern as
  // StreetDrawTool — a parent re-render (e.g. the InfrastructureLayer idle
  // reload) must not tear down the setup effect mid-draw.
  const getBuildingsRef = useRef(getBuildingGeometries);
  useEffect(() => { getBuildingsRef.current = getBuildingGeometries; }, [getBuildingGeometries]);

  // Pure overlap probe used both during draw (for live feedback) and during
  // finish (defensive). Returns true if the current ring covers positive area
  // of any building polygon. Buildings are conceptually impassable already;
  // marking them as part of an obstacle zone is meaningless and confusing.
  const computeOverlap = useCallback((verts) => {
    if (!Array.isArray(verts) || verts.length < 3) return false;
    const ring = verts.map((p) => [p.lng, p.lat]);
    ring.push([ring[0][0], ring[0][1]]);
    const geom = { type: 'Polygon', coordinates: [ring] };
    const buildings = (typeof getBuildingsRef.current === 'function'
      ? getBuildingsRef.current()
      : []) || [];
    try {
      return checkPolygon(geom, buildings).overlapsExisting;
    } catch { return false; }
  }, []);

  useEffect(() => {
    if (!map || !active) return undefined;
    const prevCursor = map.get('draggableCursor');
    map.setOptions({ draggableCursor: 'crosshair', disableDoubleClickZoom: true });
    vertsRef.current = [];
    setOverlap(false);

    const render = () => {
      const verts = vertsRef.current;
      if (!polyRef.current) {
        polyRef.current = new window.google.maps.Polygon({
          clickable: false,
          fillColor: '#dc2626',
          fillOpacity: 0.25,
          strokeColor: '#b91c1c',
          strokeWeight: 2,
          map,
        });
      }
      polyRef.current.setPath(verts);
      setCount(verts.length);
      const ov = computeOverlap(verts);
      setOverlap(ov);
      // Recolour the live ring on overlap so the operator sees the conflict
      // before they hit «اتمام». Amber means "you can keep editing; just
      // don't save it like this".
      polyRef.current.setOptions({
        strokeColor: ov ? '#f59e0b' : '#b91c1c',
        fillColor: ov ? '#fbbf24' : '#dc2626',
      });
    };
    drawRenderRef.current = render;

    const onClick = (e) => {
      if (!e.latLng) return;
      vertsRef.current.push({ lat: e.latLng.lat(), lng: e.latLng.lng() });
      render();
    };
    const cl = map.addListener('click', onClick);
    render();

    return () => {
      window.google.maps.event.removeListener(cl);
      map.setOptions({ draggableCursor: prevCursor || null, disableDoubleClickZoom: false });
      if (polyRef.current) { polyRef.current.setMap(null); polyRef.current = null; }
      drawRenderRef.current = () => {};
    };
  }, [map, active, computeOverlap]);

  const undoVertex = useCallback(() => {
    if (vertsRef.current.length === 0) return;
    vertsRef.current = vertsRef.current.slice(0, -1);
    drawRenderRef.current();
  }, []);

  const finish = useCallback(async () => {
    const verts = vertsRef.current;
    if (verts.length < 3) return;
    // Defence-in-depth: do not let an overlapping ring reach the DB even if
    // the button was clicked while the state was stale.
    if (computeOverlap(verts)) return;
    const ring = verts.map((p) => [p.lng, p.lat]);
    ring.push([ring[0][0], ring[0][1]]); // close the ring
    const geometry = { type: 'Polygon', coordinates: [ring] };
    const { error } = await createObstacle(geometry, { kind });
    if (error) {
      // eslint-disable-next-line no-alert
      window.alert('ذخیره‌ی منطقه‌ی عبورناپذیر ناموفق بود');
      return;
    }
    if (onSaved) onSaved();
    if (onClose) onClose();
  }, [kind, onSaved, onClose, computeOverlap]);

  if (!active) return null;
  const canFinish = count >= 3 && !overlap;
  return (
    <div style={{ ...HUD_BASE, borderTop: '3px solid #dc2626' }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: '#fecaca' }}>
        رسم منطقه‌ی عبورناپذیر
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {Object.entries(OBS_KIND_FA).map(([k, label]) => {
          const sel = kind === k;
          return (
            <button
              key={k}
              type="button"
              onClick={() => setKind(k)}
              style={{
                flex: 1,
                minWidth: 70,
                padding: '6px 8px',
                background: sel ? '#b91c1c' : 'rgba(51,65,85,0.55)',
                color: sel ? '#fef2f2' : '#cbd5e1',
                fontFamily: "'Vazirmatn', sans-serif",
                fontSize: 11.5,
                fontWeight: sel ? 700 : 600,
                lineHeight: 1.2,
                border: `1px solid ${sel ? 'rgba(254,242,242,0.4)' : 'rgba(148,163,184,0.28)'}`,
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: 11.5, color: '#94a3b8', lineHeight: 1.55 }}>
        روی گوشه‌های منطقه‌ی ممنوع کلیک کنید. مسیریاب از داخل این منطقه عبور نخواهد کرد.
      </div>
      <div style={{ fontSize: 11.5, color: '#cbd5e1' }}>
        {count === 0 ? 'هنوز گوشه‌ای ثبت نشده (حداقل ۳ لازم است)' : `${count} گوشه ثبت شد`}
      </div>
      {overlap && (
        <div
          style={{
            fontSize: 11.5,
            color: '#fde68a',
            background: 'rgba(120,53,15,0.55)',
            border: '1px solid rgba(251,191,36,0.45)',
            borderRadius: 6,
            padding: '6px 9px',
            lineHeight: 1.5,
          }}
        >
          این محدوده با یک ساختمان همپوشانی دارد. لطفاً گوشه‌ها را طوری تنظیم کنید که از ساختمان‌ها بگذرد.
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={finish}
          disabled={!canFinish}
          onMouseEnter={() => setHover((h) => ({ ...h, fin: true }))}
          onMouseLeave={() => setHover((h) => ({ ...h, fin: false }))}
          style={BTN_PRIMARY(hover.fin, !canFinish, 'rose')}
        >
          اتمام
        </button>
        <button
          type="button"
          onClick={undoVertex}
          disabled={count === 0}
          onMouseEnter={() => setHover((h) => ({ ...h, undo: true }))}
          onMouseLeave={() => setHover((h) => ({ ...h, undo: false }))}
          style={BTN_GHOST(hover.undo)}
        >
          حذف گوشه
        </button>
        <button
          type="button"
          onClick={onClose}
          onMouseEnter={() => setHover((h) => ({ ...h, cancel: true }))}
          onMouseLeave={() => setHover((h) => ({ ...h, cancel: false }))}
          style={BTN_GHOST(hover.cancel)}
        >
          لغو
        </button>
      </div>
    </div>
  );
}

// =========================================================================
// PoleDropTool - single-click pole drop.
// =========================================================================
const POLE_KIND_FA = {
  utility: 'تیر برق',
  fiber: 'تیر فیبر',
  telecom: 'تیر مخابرات',
  other: 'سایر',
};

export function PoleDropTool({ map, active, onClose, onSaved }) {
  const [kind, setKind] = useState('utility');
  const busyRef = useRef(false);

  useEffect(() => {
    if (!map || !active) return undefined;
    const prevCursor = map.get('draggableCursor');
    map.setOptions({ draggableCursor: 'crosshair' });

    const onClick = async (e) => {
      if (!e.latLng || busyRef.current) return;
      busyRef.current = true;
      const geometry = {
        type: 'Point',
        coordinates: [e.latLng.lng(), e.latLng.lat()],
      };
      const { error } = await createPole(geometry, { kind });
      busyRef.current = false;
      if (error) {
        // eslint-disable-next-line no-alert
        window.alert('ذخیره‌ی تیر ناموفق بود');
        return;
      }
      if (onSaved) onSaved();
    };
    const cl = map.addListener('click', onClick);

    return () => {
      window.google.maps.event.removeListener(cl);
      map.setOptions({ draggableCursor: prevCursor || null });
    };
  }, [map, active, kind, onSaved]);

  if (!active) return null;
  return (
    <div style={HUD_BASE}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: '#e2e8f0' }}>
        افزودن تیر / پایه
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {Object.entries(POLE_KIND_FA).map(([k, label]) => {
          const sel = kind === k;
          return (
            <button
              key={k}
              type="button"
              onClick={() => setKind(k)}
              style={{
                flex: 1,
                minWidth: 70,
                padding: '6px 8px',
                background: sel ? '#0ea5e9' : 'rgba(51,65,85,0.55)',
                color: sel ? '#f0f9ff' : '#cbd5e1',
                fontFamily: "'Vazirmatn', sans-serif",
                fontSize: 11.5,
                fontWeight: sel ? 700 : 600,
                lineHeight: 1.2,
                border: `1px solid ${sel ? 'rgba(240,249,255,0.4)' : 'rgba(148,163,184,0.28)'}`,
                borderRadius: 6,
                cursor: 'pointer',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: 11.5, color: '#94a3b8', lineHeight: 1.55 }}>
        روی نقشه کلیک کنید تا تیر در همان نقطه ذخیره شود. هر کلیک یک تیر می‌افزاید؛ پس از پایان «بستن» بزنید.
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={onClose}
          style={BTN_GHOST(false)}
        >
          بستن
        </button>
      </div>
    </div>
  );
}

// =========================================================================
// EntryPointTool - 2-step click flow: pick building, then pick entry point.
// =========================================================================
export function EntryPointTool({
  map, active, getBuildings, onClose, onSaved,
}) {
  const [step, setStep] = useState('pick-building');
  const [target, setTarget] = useState(null);
  const targetPolyRef = useRef(null);

  useEffect(() => {
    if (!active) {
      setStep('pick-building');
      setTarget(null);
      if (targetPolyRef.current) {
        targetPolyRef.current.setOptions({
          strokeWeight: targetPolyRef.current.__origStroke || 1,
        });
        targetPolyRef.current = null;
      }
    }
  }, [active]);

  useEffect(() => {
    if (!map || !active) return undefined;
    const prevCursor = map.get('draggableCursor');
    map.setOptions({ draggableCursor: 'crosshair' });

    const findBuildingAt = (latLng) => {
      const pt = turfPoint([latLng.lng(), latLng.lat()]);
      const list = typeof getBuildings === 'function' ? (getBuildings() || []) : [];
      for (const b of list) {
        try {
          const path = b.poly.getPath().getArray();
          const ring = path.map((ll) => [ll.lng(), ll.lat()]);
          if (ring.length < 3) continue;
          const first = ring[0];
          const last = ring[ring.length - 1];
          if (first[0] !== last[0] || first[1] !== last[1]) ring.push([first[0], first[1]]);
          const tp = turfPolygon([ring]);
          if (booleanPointInPolygon(pt, tp)) return b;
        } catch (e) { /* skip */ }
      }
      return null;
    };

    const onClick = async (e) => {
      if (!e.latLng) return;
      if (step === 'pick-building') {
        const b = findBuildingAt(e.latLng);
        if (!b) {
          // eslint-disable-next-line no-alert
          window.alert('لطفاً روی یک ساختمان کلیک کنید');
          return;
        }
        if (b.poly) {
          b.poly.__origStroke = b.poly.get('strokeWeight') || 1;
          b.poly.setOptions({ strokeWeight: 4 });
          targetPolyRef.current = b.poly;
        }
        setTarget(b);
        setStep('pick-entry');
      } else if (step === 'pick-entry' && target) {
        const geometry = {
          type: 'Point',
          coordinates: [e.latLng.lng(), e.latLng.lat()],
        };
        const { error } = await setBuildingEntry(target.id, geometry);
        if (error) {
          // eslint-disable-next-line no-alert
          window.alert('ذخیره‌ی نقطه‌ی ورود ناموفق بود');
          return;
        }
        if (onSaved) onSaved();
        if (onClose) onClose();
      }
    };
    const cl = map.addListener('click', onClick);

    return () => {
      window.google.maps.event.removeListener(cl);
      map.setOptions({ draggableCursor: prevCursor || null });
    };
  }, [map, active, step, target, getBuildings, onSaved, onClose]);

  const restart = useCallback(() => {
    if (targetPolyRef.current) {
      targetPolyRef.current.setOptions({
        strokeWeight: targetPolyRef.current.__origStroke || 1,
      });
      targetPolyRef.current = null;
    }
    setTarget(null);
    setStep('pick-building');
  }, []);

  if (!active) return null;
  return (
    <div style={{ ...HUD_BASE, borderTop: '3px solid #38bdf8' }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, color: '#e2e8f0' }}>
        تعیینِ نقطه‌ی ورودِ کابل به ساختمان
      </div>
      <div style={{ fontSize: 11.5, color: '#cbd5e1', lineHeight: 1.55 }}>
        {step === 'pick-building'
          ? 'گام ۱: روی یک ساختمان کلیک کنید'
          : `گام ۲: روی لبه‌ی همان ساختمان کلیک کنید که می‌خواهید کابل از آن وارد شود`}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        {step === 'pick-entry' && (
          <button type="button" onClick={restart} style={BTN_GHOST(false)}>
            بازانتخاب
          </button>
        )}
        <button type="button" onClick={onClose} style={BTN_GHOST(false)}>
          بستن
        </button>
      </div>
    </div>
  );
}

// =========================================================================
// InfraSelectionPanel - tiny dark-glass panel that appears when an operator
// clicks one of their own streets / obstacles / poles. Mirrors the building
// SelectionPanel's bottom-end placement so the two never collide (the user
// can have either kind selected, never both).
// =========================================================================
const INFRA_TITLE = {
  street: 'خیابان',
  obstacle: 'منطقه‌ی عبورناپذیر',
  pole: 'تیر / پایه',
};
const INFRA_DETAIL = {
  street: { main: 'خیابان اصلی', local: 'خیابان فرعی', alley: 'کوچه', pedestrian: 'پیاده‌رو' },
  obstacle: {
    river: 'رودخانه', rail: 'ریل قطار', highway: 'بزرگراه', restricted: 'محدوده‌ی ممنوع', other: 'سایر',
  },
  pole: {
    utility: 'تیر برق', fiber: 'تیر فیبر', telecom: 'تیر مخابرات', other: 'سایر',
  },
};
const INFRA_ACCENT = {
  street: '#2563eb',
  obstacle: '#dc2626',
  pole: '#0ea5e9',
};

export function InfraSelectionPanel({
  item, editing = false, onEdit, onSave, onCancel, onDelete, onClose,
}) {
  const [delHover, setDelHover] = useState(false);
  const [closeHover, setCloseHover] = useState(false);
  const [editHover, setEditHover] = useState(false);
  const [saveHover, setSaveHover] = useState(false);
  const [cancelHover, setCancelHover] = useState(false);
  if (!item) return null;
  const accent = INFRA_ACCENT[item.kind] || '#0d9488';
  const title = INFRA_TITLE[item.kind] || 'مورد';
  const detail = INFRA_DETAIL[item.kind]?.[item.subKind] || '';
  // Edit hint per kind — operators told us they expected the same vertex-
  // drag affordance buildings have. Hint surfaces what to drag, kind-specific.
  const editHint = {
    street: 'برای جابه‌جایی گوشه‌های خیابان روی نقاط سفید بکشید، سپس «ذخیره» را بزنید.',
    obstacle: 'برای جابه‌جایی گوشه‌های منطقه روی نقاط سفید بکشید، سپس «ذخیره» را بزنید.',
    pole: 'تیر را به نقطه‌ی دلخواه بکشید، سپس «ذخیره» را بزنید.',
  }[item.kind];
  return (
    <div
      style={{
        position: 'absolute',
        bottom: 22,
        insetInlineEnd: 14,
        zIndex: 7,
        display: 'flex',
        flexDirection: 'column',
        gap: 11,
        minWidth: 244,
        padding: '13px 15px',
        background: 'linear-gradient(135deg, rgba(15,23,42,0.95) 0%, rgba(30,41,59,0.93) 100%)',
        borderRadius: 13,
        border: '1px solid rgba(148,163,184,0.24)',
        borderTop: `3px solid ${accent}`,
        boxShadow:
          '0 1px 0 rgba(255,255,255,0.07) inset, 0 10px 30px rgba(2,6,23,0.6), 0 1px 3px rgba(0,0,0,0.45)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        direction: 'rtl',
        fontFamily: "'Vazirmatn', sans-serif",
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 9,
          fontSize: 12.5,
          fontWeight: 700,
          color: '#e2e8f0',
          lineHeight: 1.4,
        }}
      >
        <span
          aria-hidden="true"
          style={{
            display: 'inline-block',
            width: 9,
            height: 9,
            borderRadius: '50%',
            background: accent,
            boxShadow: `0 0 0 3px ${accent}22`,
            flexShrink: 0,
          }}
        />
        <span>
          {title}
          {detail ? ` · ${detail}` : ''}
          {item.canManage ? '' : ` · ${item.ownerName || 'اپراتور دیگر'}`}
        </span>
      </div>
      {editing && editHint && (
        <div style={{ fontSize: 11.5, color: '#cbd5e1', lineHeight: 1.55 }}>
          {editHint}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8 }}>
        {editing && item.canManage ? (
          <>
            <button
              type="button"
              onClick={onSave}
              onMouseEnter={() => setSaveHover(true)}
              onMouseLeave={() => setSaveHover(false)}
              style={{
                flex: 1,
                padding: '9px 14px',
                background: saveHover
                  ? 'linear-gradient(135deg, #14b8a6 0%, #0d9488 100%)'
                  : 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)',
                color: '#f0fdfa',
                fontFamily: "'Vazirmatn', sans-serif",
                fontSize: 13,
                fontWeight: 700,
                lineHeight: 1,
                border: '1px solid rgba(45,212,191,0.4)',
                borderRadius: 8,
                cursor: 'pointer',
                transition: 'background 140ms ease',
              }}
            >
              ذخیره
            </button>
            <button
              type="button"
              onClick={onCancel}
              onMouseEnter={() => setCancelHover(true)}
              onMouseLeave={() => setCancelHover(false)}
              style={{
                flex: 1,
                padding: '9px 14px',
                background: cancelHover ? 'rgba(71,85,105,0.7)' : 'rgba(51,65,85,0.55)',
                color: '#e2e8f0',
                fontFamily: "'Vazirmatn', sans-serif",
                fontSize: 13,
                fontWeight: 600,
                lineHeight: 1,
                border: '1px solid rgba(148,163,184,0.32)',
                borderRadius: 8,
                cursor: 'pointer',
                transition: 'background 140ms ease',
              }}
            >
              لغو
            </button>
          </>
        ) : (
          <>
            {item.canManage && (
              <>
                <button
                  type="button"
                  onClick={onEdit}
                  onMouseEnter={() => setEditHover(true)}
                  onMouseLeave={() => setEditHover(false)}
                  style={{
                    flex: 1,
                    padding: '9px 14px',
                    background: editHover
                      ? 'linear-gradient(135deg, #38bdf8 0%, #0ea5e9 100%)'
                      : 'rgba(14,165,233,0.22)',
                    color: editHover ? '#f0f9ff' : '#bae6fd',
                    fontFamily: "'Vazirmatn', sans-serif",
                    fontSize: 13,
                    fontWeight: 700,
                    lineHeight: 1,
                    border: editHover
                      ? '1px solid rgba(56,189,248,0.55)'
                      : '1px solid rgba(56,189,248,0.32)',
                    borderRadius: 8,
                    cursor: 'pointer',
                    transition: 'background 140ms ease, color 140ms ease',
                  }}
                >
                  ویرایش
                </button>
                <button
                  type="button"
                  onClick={onDelete}
                  onMouseEnter={() => setDelHover(true)}
                  onMouseLeave={() => setDelHover(false)}
                  style={{
                    flex: 1,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 7,
                    padding: '9px 14px',
                    background: delHover
                      ? 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)'
                      : 'rgba(127,29,29,0.42)',
                    color: delHover ? '#fef2f2' : '#fecaca',
                    fontFamily: "'Vazirmatn', sans-serif",
                    fontSize: 13,
                    fontWeight: 700,
                    lineHeight: 1,
                    border: delHover
                      ? '1px solid rgba(248,113,113,0.55)'
                      : '1px solid rgba(248,113,113,0.32)',
                    borderRadius: 8,
                    cursor: 'pointer',
                    transition: 'background 140ms ease, color 140ms ease',
                  }}
                >
                  <span aria-hidden="true" style={{ fontWeight: 800 }}>×</span>
                  <span>حذف</span>
                </button>
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              onMouseEnter={() => setCloseHover(true)}
              onMouseLeave={() => setCloseHover(false)}
              style={{
                flex: item.canManage ? undefined : 1,
                padding: '9px 14px',
                background: closeHover ? 'rgba(71,85,105,0.7)' : 'rgba(51,65,85,0.55)',
                color: '#e2e8f0',
                fontFamily: "'Vazirmatn', sans-serif",
                fontSize: 13,
                fontWeight: 600,
                lineHeight: 1,
                border: '1px solid rgba(148,163,184,0.32)',
                borderRadius: 8,
                cursor: 'pointer',
                transition: 'background 140ms ease',
              }}
            >
              بستن
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// =========================================================================
// InfrastructureLayer - fetches + renders streets / obstacles / poles /
// building entry-point dots on the map, alongside the building polygons.
// The buildings themselves stay rendered by AnnotationMap's reload(); this
// layer adds the three new resources and the entry-point markers.
// =========================================================================
export function InfrastructureLayer({
  map, reloadKey, onSnapPoints, onSelectInfra, getCanInteract, isPaused,
}) {
  const streetsRef = useRef([]); // Polyline objects
  const obstaclesRef = useRef([]); // Polygon objects
  const polesRef = useRef([]); // marker DOM/AdvancedMarker objects
  const streetTipRef = useRef(null); // shared InfoWindow: street name on hover
  // Stable ref so the idle listener registered once can read the latest
  // pause flag without re-binding (and tearing down the listener every
  // time the operator clicks into / out of edit mode).
  const isPausedRef = useRef(isPaused);
  useEffect(() => { isPausedRef.current = isPaused; }, [isPaused]);

  const clearAll = useCallback(() => {
    streetsRef.current.forEach((p) => p.setMap(null));
    obstaclesRef.current.forEach((p) => p.setMap(null));
    polesRef.current.forEach((m) => {
      if (m && typeof m.setMap === 'function') m.setMap(null);
      else if (m) m.map = null;
    });
    streetsRef.current = [];
    obstaclesRef.current = [];
    polesRef.current = [];
    if (streetTipRef.current) streetTipRef.current.close();
  }, []);

  const reload = useCallback(async () => {
    if (!map) return;
    // While the operator is mid-edit on an infra item, clearAll would
    // setMap(null) on their editable overlay and silently destroy their
    // work. Skip; the next non-paused idle (post-save/cancel) will refresh.
    if (typeof isPausedRef.current === 'function'
      ? isPausedRef.current()
      : !!isPausedRef.current) return;
    const b = map.getBounds();
    if (!b) return;
    const j = b.toJSON();

    clearAll();

    const [s, o, p] = await Promise.all([
      streetsInBbox(j),
      obstaclesInBbox(j),
      polesInBbox(j),
    ]);

    // streets
    const snapPts = [];
    (s.rows || []).forEach((row) => {
      const coords = row.geometry && row.geometry.coordinates;
      if (!Array.isArray(coords) || coords.length < 2) return;
      const path = coords.map(([lng, lat]) => ({ lat, lng }));
      const color = STREET_CLASS_COLOR[row.class] || '#64748b';
      const isPed = row.class === 'pedestrian';
      const line = new window.google.maps.Polyline({
        path,
        strokeColor: color,
        strokeOpacity: isPed ? 0 : 0.85,
        strokeWeight: row.class === 'main' ? 4 : 3,
        icons: isPed ? [{
          icon: { path: 'M 0,-1 0,1', strokeOpacity: 1, scale: 2, strokeColor: color },
          offset: '0', repeat: '8px',
        }] : undefined,
        clickable: true,
        zIndex: 5,
        map,
      });
      line.__streetId = row.id;
      line.__canManage = row.can_manage;
      line.__geometry = row.geometry;
      line.__name = row.name || null;
      // Hover tooltip: show the (Persian) street name when the source carries
      // one (e.g. imported OSM names like «بلوار بسیج»). Follows the cursor.
      if (row.name) {
        const showTip = (e) => {
          if (!streetTipRef.current) {
            streetTipRef.current = new window.google.maps.InfoWindow({ disableAutoPan: true });
          }
          const el = document.createElement('div');
          el.textContent = row.name;
          el.style.cssText = 'direction:rtl;font-family:Vazirmatn,Vazir,Tahoma,sans-serif;'
            + 'font-size:12px;font-weight:600;color:#0f172a;white-space:nowrap;padding:1px 3px;';
          streetTipRef.current.setContent(el);
          if (e && e.latLng) streetTipRef.current.setPosition(e.latLng);
          streetTipRef.current.open({ map });
        };
        line.addListener('mouseover', showTip);
        line.addListener('mousemove', (e) => {
          if (streetTipRef.current && e && e.latLng) streetTipRef.current.setPosition(e.latLng);
        });
        line.addListener('mouseout', () => {
          if (streetTipRef.current) streetTipRef.current.close();
        });
      }
      line.addListener('click', () => {
        if (typeof getCanInteract === 'function' && !getCanInteract()) return;
        if (typeof onSelectInfra !== 'function') return;
        onSelectInfra({
          kind: 'street',
          id: row.id,
          obj: line,
          geometry: row.geometry,
          canManage: !!row.can_manage,
          ownerName: row.owner_name || null,
          subKind: row.class,
        });
      });
      streetsRef.current.push(line);
      // Snap targets = endpoints of each street (first + last node).
      snapPts.push(path[0]);
      snapPts.push(path[path.length - 1]);
    });
    if (onSnapPoints) onSnapPoints(snapPts);

    // obstacles - clickable so the owner / admin can delete them, but with a
    // low zIndex so a building polygon sitting on top of an obstacle still
    // wins the click (the more specific selection target).
    (o.rows || []).forEach((row) => {
      const coords = row.geometry && row.geometry.coordinates && row.geometry.coordinates[0];
      if (!Array.isArray(coords) || coords.length < 4) return;
      const path = coords.slice(0, -1).map(([lng, lat]) => ({ lat, lng }));
      const poly = new window.google.maps.Polygon({
        paths: path,
        fillColor: '#dc2626',
        fillOpacity: 0.16,
        strokeColor: '#b91c1c',
        strokeOpacity: 0.7,
        strokeWeight: 1.5,
        clickable: true,
        zIndex: 1,
        map,
      });
      poly.__obstacleId = row.id;
      poly.__canManage = row.can_manage;
      poly.__geometry = row.geometry;
      poly.addListener('click', () => {
        if (typeof getCanInteract === 'function' && !getCanInteract()) return;
        if (typeof onSelectInfra !== 'function') return;
        onSelectInfra({
          kind: 'obstacle',
          id: row.id,
          obj: poly,
          geometry: row.geometry,
          canManage: !!row.can_manage,
          ownerName: row.owner_name || null,
          subKind: row.kind,
        });
      });
      obstaclesRef.current.push(poly);
    });

    // poles - small clickable circle markers. Radius 2.5 m so they remain a
    // findable click target at the typical operator zoom range (16 to 20).
    (p.rows || []).forEach((row) => {
      const coords = row.geometry && row.geometry.coordinates;
      if (!Array.isArray(coords) || coords.length < 2) return;
      const [lng, lat] = coords;
      const c = new window.google.maps.Circle({
        center: { lat, lng },
        radius: 2.5,
        fillColor: '#0ea5e9',
        fillOpacity: 0.95,
        strokeColor: '#f0f9ff',
        strokeOpacity: 1,
        strokeWeight: 1.5,
        clickable: true,
        zIndex: 10,
        map,
      });
      c.__poleId = row.id;
      c.__canManage = row.can_manage;
      c.__geometry = row.geometry;
      c.addListener('click', () => {
        if (typeof getCanInteract === 'function' && !getCanInteract()) return;
        if (typeof onSelectInfra !== 'function') return;
        onSelectInfra({
          kind: 'pole',
          id: row.id,
          obj: c,
          geometry: row.geometry,
          canManage: !!row.can_manage,
          ownerName: row.owner_name || null,
          subKind: row.kind,
        });
      });
      polesRef.current.push(c);
    });
  }, [map, clearAll, onSnapPoints, onSelectInfra, getCanInteract]);

  useEffect(() => {
    if (!map) return undefined;
    const l = map.addListener('idle', reload);
    reload();
    return () => {
      window.google.maps.event.removeListener(l);
      clearAll();
    };
  }, [map, reload, clearAll]);

  // External reload trigger: when a tool saves a new piece, parent bumps
  // reloadKey and we redraw without waiting for a map idle.
  useEffect(() => {
    if (reloadKey === 0) return;
    reload();
  }, [reloadKey, reload]);

  return null;
}

// Exposes helper handlers (delete) to AnnotationMap's polygon click logic.
export async function deleteStreetById(id) {
  const { error } = await deleteStreet(id);
  return { error };
}
export async function deleteObstacleById(id) {
  const { error } = await deleteObstacle(id);
  return { error };
}
export async function deletePoleById(id) {
  const { error } = await deletePole(id);
  return { error };
}
